import { notify } from "../components/ui";
import { t } from "../i18n";
import { showSuccess, toAppError } from "../lib/feedback";
import { useApi } from "./useApi";
import { reservaService } from "../services/reservas";
import {
  Reserva,
  ReservaCreatePayload,
  ReservaUpdatePayload,
  DoctorReservasResponse,
} from "../types/reserva.types";
import { useSession } from "../context/SessionContext";
import { SUCCESS_MESSAGES } from "../i18n/success.pt-BR";

type LinkOutcome = "linked" | "already-linked";

export function useReservas() {
  const { metadata } = useSession();

  const searchApi = useApi<Reserva[]>();
  const createApi = useApi<Reserva>();
  const updateApi = useApi<Reserva>();
  const listApi   = useApi<DoctorReservasResponse>();
  const linkApi   = useApi<LinkOutcome>();
  const unlinkApi = useApi<void>();

  // Autocomplete: não mostra toast de erro a cada tecla digitada,
  // uma falha de rede aqui não deve interromper o usuário.
  const searchReservas = async (query: string) => {
    return searchApi.request(
      () => reservaService.search(searchApi.apiFetch, query),
      { showErrorToast: false },
    );
  };

  const createReserva = async (payload: ReservaCreatePayload) => {
    return createApi.request(
      () => reservaService.create(createApi.apiFetch, payload),
    );
  };

  // Edição é sobre a reserva em si (não sobre o vínculo) — pode afetar outros
  // médicos que compartilham a mesma reserva (dedup por nome no cadastro). O
  // `detail` de um 422 (ex: nome vazio) já chega pronto pro usuário via toast
  // automático do useApi.
  const updateReserva = async (reservaId: number, payload: ReservaUpdatePayload) => {
    return updateApi.request(
      () => reservaService.update(updateApi.apiFetch, reservaId, payload),
    );
  };

  const listMyReservas = async () => {
    if (!metadata.email) return null;

    return listApi.request(
      () => reservaService.listForDoctor(listApi.apiFetch, metadata.email!),
    );
  };

  // Um 409 aqui só significa que a reserva já está vinculada a este médico —
  // não é uma falha real, então é tratado dentro do próprio `fn` (sem lançar)
  // para não disparar o toast de erro automático do `request`, e sim um aviso
  // neutro. Qualquer outro erro (404, 500, rede) continua sendo propagado
  // normalmente para o tratamento padrão.
  const linkReserva = async (reservaId: number): Promise<boolean> => {
    if (!metadata.email) return false;

    const outcome = await linkApi.request(async () => {
      try {
        await reservaService.linkToDoctor(linkApi.apiFetch, metadata.email!, { reserva_id: reservaId });
        return "linked";
      } catch (caught) {
        const err = toAppError(caught);
        if (err?.status === 409) return "already-linked";
        throw caught;
      }
    });

    if (outcome === null) return false;

    if (outcome === "already-linked") {
      notify({ tone: "info", title: t("links.reserva.alreadyLinked") });
    } else {
      showSuccess("reservaLinked");
    }
    return true;
  };

  const unlinkReserva = async (reservaId: number) => {
    if (!metadata.email) return null;

    return unlinkApi.request(
      () => reservaService.unlinkFromDoctor(unlinkApi.apiFetch, metadata.email!, reservaId),
      { successMessage: SUCCESS_MESSAGES.reservaUnlinked },
    );
  };

  return {
    searchReservas,
    createReserva,
    updateReserva,
    listMyReservas,
    linkReserva,
    unlinkReserva,
    searching: searchApi.loading,
    creating:  createApi.loading,
    updating:  updateApi.loading,
    listing:   listApi.loading,
    linking:   linkApi.loading,
    unlinking: unlinkApi.loading,
  };
}
